<template>
  <div
    ref="uploadContainerRef"
    class="image-upload-wrap relative border border-solid"
    :class="[state.pasteMode ? 'border-[#01a887]' : 'border-[#DFE5EC]']"
    :style="{ width: width, height: height }"
    tabindex="0"
    @paste="handlePaste"
  >
    <MUpload
      class="upload mupload flex h-full w-full cursor-pointer items-center bg-[#F6F7F8] bg-center bg-no-repeat"
      upload-type="img"
      :disabled="disabled"
      :maxSize="maxSize"
      :maxCount="1"
      :style="{
        backgroundImage: 'url(' + imgPath + ')',
      }"
      @upload-success="uploadSuccess"
    >
      <div
        v-show="state.pasteMode"
        class="top-0px absolute left-0 right-0 z-20 mx-auto flex justify-center bg-[rgba(255,255,255,0.8)] text-[#01a887]"
      >
        ctrl+v {{ t('c.25090306') }}
      </div>
      <div
        v-show="!state.pasteMode"
        class="bottom-6px gap-20px upload-button-group absolute left-0 right-0 z-20 mx-auto flex justify-center"
      >
        <div
          v-show="hasPhoto"
          class="upload-btn"
          :loading="taking === 1"
          @click.stop="takePictures"
        >
          {{ t('ML_20250523_9') }}
        </div>
        <div class="upload-btn" @click="toggleFileMode">
          {{ t('c.25090365') }}
        </div>
        <div class="upload-btn" @click.stop="togglePasteMode">
          {{ t('c.25090306') }}
        </div>
      </div>
    </MUpload>

    <div class="top-50% z-1 absolute h-full translate-y-[-50%]" :style="{ width: width }">
      <div v-show="!imgPath" class="flex h-full flex-col items-center justify-center">
        <div class="text-14px text-[#8991A1]"> {{ t('c.25090365') }}</div>
      </div>
    </div>

    <!-- 视频预览区域 -->
    <div v-show="taking === 2" class="camera-preview">
      <video ref="videoRef" autoplay playsinline class="camera h-full"></video>
      <!-- 照片预览 -->
      <canvas v-show="false" ref="canvasRef"></canvas>
      <canvas v-show="false" ref="canvas2Ref"></canvas>
      <MButton color="green" class="ok-button" @click="takeOk"> {{ t('ML_20250523_9') }}</MButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import axios from 'axios';
  import { onMounted, onUnmounted, reactive, ref } from 'vue';
  import { message } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { MUpload } from '@mts/render/components/Upload';
  import { getRequestHeaders } from '@mts/utils/auth';
  import { globSetting } from '@mts/utils/env';
  import type { ImageState, ImageUploadProps } from './types';

  const emit = defineEmits(['uploadSuccess', 'uploading', 'update:value', 'change', 'done']);
  withDefaults(defineProps<ImageUploadProps>(), {
    width: '100%',
    height: '100%',
    maxSize: 20,
    disabled: false,
    hasPhoto: false,
  });

  const ACTION_URL = {
    LOWCODE: '/low-code/lowCode/config/file/upload',
    IMS: '/ims-system/system/file/upload',
  };
  const action = globSetting.apiUrl + ACTION_URL['IMS'];

  const state = reactive<ImageState>({
    pasteMode: false,
    file: null,
    uploading: false,
  });

  const uploadContainerRef = ref<HTMLDivElement>();

  const videoRef = ref();
  const canvasRef = ref();
  const canvas2Ref = ref();
  const taking = ref(0);
  // 初始化摄像头
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // 使用后置摄像头
        audio: false,
      });
      videoRef.value.srcObject = stream;
      // isCameraReady.value = true;
      // statusMessage.value = '摄像头已就绪';
    } catch (error) {
      console.error('无法访问摄像头:', error);
      taking.value = 0;
      // statusMessage.value = '无法访问摄像头: ' + error.message;
    }
  };
  const takePictures = async () => {
    taking.value = 1;
    await startCamera();
    taking.value = 2;
  };

  const takeOk = () => {
    console.log('123123123');
    const video = videoRef.value;
    const canvas = canvasRef.value;
    // 设置canvas尺寸与视频一致
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    // 绘制当前视频帧到canvas
    const context = canvas.getContext('2d');
    console.log(video, video.videoWidth, video.videoHeight);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    cropTo3x4();
  };

  // 按3:4比例裁剪
  const cropTo3x4 = () => {
    const source = canvasRef.value;
    const dest = canvas2Ref.value;

    // 计算3:4裁剪区域
    let srcWidth = source.width;
    let srcHeight = source.height;

    // 目标比例
    const targetRatio = 3 / 4;

    // 计算裁剪区域
    let cropX = 0,
      cropY = 0;
    let cropWidth = srcWidth,
      cropHeight = srcHeight;

    // 当前图片比例
    const currentRatio = srcWidth / srcHeight;

    if (currentRatio > targetRatio) {
      // 图片太宽，需要裁剪左右
      cropWidth = srcHeight * targetRatio;
      cropX = (srcWidth - cropWidth) / 2;
    } else {
      // 图片太高，需要裁剪上下
      cropHeight = srcWidth / targetRatio;
      cropY = (srcHeight - cropHeight) / 2;
    }

    // 设置目标canvas尺寸 (可以指定具体尺寸，如300x400)
    dest.width = 300;
    dest.height = 400;

    // 执行裁剪
    const destCtx = dest.getContext('2d');
    destCtx.drawImage(
      source,
      cropX,
      cropY, // 源图像裁剪起始坐标
      cropWidth,
      cropHeight, // 源图像裁剪区域尺寸
      0,
      0, // 目标canvas起始坐标
      cropWidth,
      cropHeight, // 目标canvas绘制尺寸
    );

    // 将canvas转为Blob对象
    dest.toBlob(
      (blob) => {
        const file = new File([blob], 'image.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        taking.value = 0;
        if (videoRef.value && videoRef.value.srcObject) {
          const stream = videoRef.value.srcObject;
          const tracks = stream.getTracks();
          tracks.forEach((track) => track.stop());
        }

        state.file = file;
        uploadImage();
        console.log('拍照成功', file);
      },
      'image/jpeg',
      0.95,
    );
  };

  const toggleFileMode = () => {
    state.pasteMode = false;
  };

  const togglePasteMode = async () => {
    state.pasteMode = true;
    if (uploadContainerRef.value) {
      uploadContainerRef.value.focus();
    }
  };

  const handlePaste = async (event) => {
    if (!state.pasteMode) return;
    console.group('ctrl+v 粘贴图片');
    try {
      const clipboardData = event.clipboardData || window.clipboardData;
      const items = clipboardData.items;

      if (!items || items.length === 0) {
        message.error(t('c.25090307'));
        return;
      }

      let file = null;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          file = item.getAsFile();
          break;
        }
      }

      if (!file) {
        message.error(t('c.25090308'));
        return;
      }

      console.log('粘贴的图片文件:', file);
      state.file = file;
      uploadImage();
    } catch (error) {
      console.error('剪切板读取失败', error);
      message.error(t('c.25090309'));
    }
    console.groupEnd();

    /*  if (!state.pasteMode) return;
    console.group('ctrl+v 粘贴图片');
    try {
      const imgs = await navigator.clipboard.read();
      if (imgs.length > 0) {
        const img = imgs[0];
        const isImg = img.types.some((type) => type.includes('image'));
        if (!isImg) {
          message.error('粘贴内容非图片');
          return false;
        }
        const blob = await img.getType(img.types[0]);
        state.file = new File([blob], 'pasted-image.png', { type: blob.type });
        console.log('file:', state.file);

        if (props.maxSize && state.file.size / 1024 / 1024 > props.maxSize) {
          message.error(t('component.upload.maxSizeMultiple', [props.maxSize]));
          return false;
        }
        uploadImage();
      } else {
        message.error('剪切板为空');
      }
    } catch (err) {
      console.error('剪切板读取失败', err);
      message.error('无法读取剪切板内容');
    }

    console.groupEnd(); */
  };

  async function uploadImage() {
    const formData = new FormData();
    formData.append('file', state.file);
    formData.append('bucketName', 'pic-file-bucket');
    formData.append('makeFileNameEndWithTimestamp', true);
    formData.append('randomFileName', false);

    state.uploading = true;
    try {
      const res = await axios.post(action, formData, {
        headers: {
          ...getRequestHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res?.data?.data) {
        const { uploadKey, fileName } = res.data.data;
        const resFile = {
          fileUrl: uploadKey,
          fileName: fileName,
        };
        console.log('上传成功 resFile', resFile);

        emit('uploadSuccess', resFile);
      } else {
        message.error(t('component.upload.uploadError'));
      }
    } catch (error) {
      console.error('上传失败', error);
      message.error(t('component.upload.uploadError'));
    } finally {
      state.file = null;
      state.uploading = false;
    }
  }

  function uploadSuccess(file) {
    console.log('上传成功', file);
    emit('uploadSuccess', file);
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (uploadContainerRef.value && !uploadContainerRef.value.contains(event.target as Node)) {
      state.pasteMode = false;
    }
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<style scoped lang="less">
  .image-upload-wrap {
    overflow: hidden;

    .mupload {
      background-size: 100% 100%;
    }

    .upload-button-group {
      transform: translateY(130%);
      transition: all 0.15s linear;
    }

    .upload-btn {
      padding: 2px 15px;
      border: 1px solid #01a887;
      border-radius: 4px;
      background-color: #fff;
      color: #01a887;
    }

    &:hover {
      .upload-button-group {
        transform: translateY(0);
      }
    }
  }

  .camera-preview {
    display: block;
    position: fixed;
    z-index: 9999;
    top: 50%;
    left: 50%;
    height: 400px;
    margin: 10px auto;
    transform: translate(-50%, -50%);
    border: 1px solid #ddd;
    background-color: #f5f5f5;

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 50%;
      width: 300px;
      height: 400px;
      transform: translateX(-50%);
      border: 2px dashed red;
    }

    .ok-button {
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
    }
  }
</style>
